import chrono from "chrono-node";
import * as luxon from "luxon";
import React, { useContext, useState } from "react";
import { time_format_utils } from "../../../../../common/time_format_utils";
import { DashboardUtils } from "../../dashboard_utils";
import { EventCreationContext } from "../../event_creation";
import { DateTimePicker } from "../date_time_picker";
import { ModifySettingsButton } from "./base_components/modify_settings_button";
import { SeshTextInput } from "./base_components/sesh_text_input";
import { SeshTextRawInput } from "./base_components/sesh_text_raw_input";
import { EventDurationComponent } from "./duration_component";

interface DateFormProp {
	id?: string;
	name?: string;
	initial_value: luxon.DateTime;
	updateFunc: Function;
	onInputChange?: (str: string) => void;
	personal_zone: string;
	guild_zone: string;
	placeholder: string;
	use_24h_clock: boolean;
	display_text: string;
	duration_ms: number | undefined;
	disabled?: boolean;
	info?: string;
}

interface DateFormState {
	value: string;
	utc_date: luxon.DateTime;
	is_duration_active: boolean;
}

export function DatePickerButton(props: {
	initial_date: Date;
	onSubmit: (date: Date) => void;
	modal_subtitle: string;
}) {
	const [isActive, setIsActive] = useState(false);
	const [date, setDate] = useState(props.initial_date);

	const modal_body = (
		<div className="columns is-mobile is-multiline is-centered">
			<div className="column is-two-thirds-tablet is-full-mobile is-centered">
				<DateTimePicker
					updateFunc={(date) => setDate(date)}
					initial_value={date}
					disable_past_days={true}
				/>
			</div>
			<div className="column is-full">
				<div className="is-pulled-right">
					<button
						className={`button dashboard-primary-button--tiny is-size-6 has-text-right`}
						type="submit"
						onClick={(e) => {
							e.preventDefault();
							props.onSubmit(date);
							setIsActive(false);
						}}
					>
						<span className="has-text-weight-bold">Confirm</span>
					</button>
				</div>
			</div>
		</div>
	);

	return (
		<>
			<div className="flex h-full w-full items-center p-2 text-2xl">
				<div
					onClick={() => {
						setIsActive(true);
					}}
				>
					<i className="far fa-calendar-alt text-sesh-primary hover:cursor-pointer hover:text-sesh-dark"></i>
				</div>
			</div>
			<DashboardUtils.SeshModal
				is_active={isActive}
				onClose={(e) => {
					e.preventDefault();
					setIsActive(false);
				}}
				title={"Date Picker"}
				allow_overflow
				subtitle={props.modal_subtitle}
				content={modal_body}
			/>
		</>
	);
}

export class DateFormWithDuration extends React.Component<
	DateFormProp,
	DateFormState
> {
	constructor(props) {
		super(props);

		let initial_date: luxon.DateTime = props.initial_value;
		let initial_value = initial_date
			? time_format_utils.format_time_verbose_without_until(
					initial_date.setZone(props.personal_zone),
					null,
					this.props.use_24h_clock
			  )
			: "";

		this.state = {
			value: initial_value,
			utc_date: props.initial_value,
			is_duration_active: false,
		};

		this.handleInputChange = this.handleInputChange.bind(this);
	}

	// TODO: Storing redundant state on both this form and its parent, fix??
	handleInputChange(event) {
		this.props.onInputChange?.(event.target.value);
		this.updateDate(event.target.value);
	}

	updateDateFromPicker(date: Date) {
		const luxon_date = luxon.DateTime.fromJSDate(date);

		const value =
			time_format_utils.format_time_from_date_picker(luxon_date);

		this.props.onInputChange?.(value);

		const utc_date = luxon_date.toUTC();

		this.setState({
			value,
			utc_date,
		});
		this.props.updateFunc(utc_date);
	}

	updateDate(str: string) {
		const message_timestamp_luxon = luxon.DateTime.fromJSDate(
			new Date()
		).toLocal();

		const local_timezone = luxon.DateTime.local().zone;
		const user_timezone = luxon.IANAZone.create(this.props.personal_zone);

		const user_timezone_offset = -local_timezone.offset(
			message_timestamp_luxon.toMillis()
		);

		const guild_timezone_offset = -user_timezone.offset(
			message_timestamp_luxon.toMillis()
		);

		let chrono_results = chrono.parse(
			str,
			message_timestamp_luxon
				.plus({
					minutes: user_timezone_offset - guild_timezone_offset,
				})
				.toJSDate(),
			{
				forwardDate: true,
			}
		);

		let chrono_result = chrono_results[0];

		if (this.props.use_24h_clock) {
			time_format_utils.refine_to_24_hour_clock(chrono_result);
		}

		const start_time_parse_success =
			chrono_results !== undefined &&
			chrono_result !== undefined &&
			chrono_result.start !== undefined;

		let luxon_date: luxon.DateTime = null;
		if (start_time_parse_success) {
			if (chrono_result.start.isCertain("timezoneOffset")) {
				luxon_date = luxon.DateTime.fromJSDate(
					chrono_result.start.date()
				).toUTC(chrono_result.start.get("timezoneOffset"));
			} else {
				luxon_date = luxon.DateTime.fromJSDate(
					chrono_result.start.date()
				)
					.minus(
						luxon.Duration.fromObject({
							minutes:
								user_timezone_offset - guild_timezone_offset,
						})
					)
					.toUTC();
			}

			if (!luxon_date.isValid) {
				luxon_date = null;
			}
		}

		this.setState({
			value: str,
			utc_date: luxon_date,
		});
		this.props.updateFunc(luxon_date);
	}

	render() {
		let start_time_personal: luxon.DateTime;
		let start_time_guild: luxon.DateTime;

		if (this.state.utc_date) {
			start_time_personal = this.state.utc_date.setZone(
				this.props.personal_zone
			);
			start_time_guild = this.state.utc_date.setZone(
				this.props.guild_zone
			);
		} else {
			start_time_personal = luxon.DateTime.local();
			start_time_guild = luxon.DateTime.local();
		}

		const timezone_abbr_personal = time_format_utils.get_timezone_abbr(
			start_time_personal,
			this.props.personal_zone
		);
		const timezone_abbr_guild = time_format_utils.get_timezone_abbr(
			start_time_guild,
			this.props.guild_zone
		);

		let does_personal_timezone_match_guild =
			time_format_utils.areTimezonesEquivalent(
				this.props.personal_zone,
				this.props.guild_zone,
				this.state.utc_date
			);

		let timezone_delta_message = null;
		if (!does_personal_timezone_match_guild) {
			// timezone_delta_message = (
			// 	<div className="warning-note">
			// 		<p>{`Please enter a time in your local timezone (${timezone_abbr_personal})`}</p>
			// 		<p>{`It will be converted to the server timezone (${timezone_abbr_guild})`}</p>
			// 	</div>
			// );
		}

		let output_message = null;

		const is_field_empty =
			!this.state.value || this.state.value.length === 0;
		const is_time_valid = Boolean(this.state.utc_date);

		if (!is_field_empty) {
			if (is_time_valid) {
				let guild_time_text: string;
				if (this.props.display_text) {
					guild_time_text = time_format_utils.format_time_verbose(
						start_time_guild,
						null,
						null,
						this.props.use_24h_clock,
						this.props.duration_ms
					);
				} else {
					guild_time_text = time_format_utils.format_time_verbose(
						start_time_guild,
						this.props.personal_zone,
						null,
						this.props.use_24h_clock,
						this.props.duration_ms
					);
				}

				let success_message: JSX.Element;

				if (does_personal_timezone_match_guild) {
					success_message = (
						<span>
							{this.props.display_text && (
								<p>
									<b>
										{this.props.display_text} (
										{timezone_abbr_guild})
									</b>
								</p>
							)}
							<p>{` ${guild_time_text}`}</p>
						</span>
					);
				} else {
					const is_same_day = start_time_personal.hasSame(
						start_time_guild,
						"day"
					);

					if (is_same_day) {
						const dual_timezone_text =
							time_format_utils.format_time_verbose_dual_timezone(
								start_time_personal,
								this.props.personal_zone,
								this.props.guild_zone,
								this.props.use_24h_clock,
								this.props.duration_ms
							);
						success_message = (
							<span>
								<p>
									<b>{this.props.display_text}</b>
								</p>
								<p>{` ${dual_timezone_text}`}</p>
							</span>
						);
					} else {
						const local_time_text =
							time_format_utils.format_time_verbose(
								start_time_personal,
								null,
								null,
								this.props.use_24h_clock,
								this.props.duration_ms
							);
						success_message = (
							<div className="flex flex-col gap-2 sm:flex-row sm:gap-8">
								<div className="output-time">
									<p>
										<b>
											Local time ({timezone_abbr_personal}
											)
										</b>
									</p>
									<p>{` ${local_time_text}`}</p>
								</div>
								<div className="output-time">
									<p>
										<b>
											Server time ({timezone_abbr_guild})
										</b>
									</p>
									<p>{` ${guild_time_text}`}</p>
								</div>
							</div>
						);
					}
				}
				const in_future =
					this.state.utc_date.toMillis() > new Date().getTime();
				if (in_future) {
					output_message = (
						<div className="success-note mt-4">
							{success_message}
						</div>
					);
				} else {
					output_message = (
						<div className="error-note mt-4">
							<p className="error-text has-text-weight-bold">
								Invalid Input: Time in past
							</p>
							{success_message}
						</div>
					);
				}
			} else {
				output_message = (
					<div className="error-note mt-4">
						<p>Could not parse time</p>
					</div>
				);
			}
		}

		let inputClassName = "input";
		if (this.props.disabled) {
			inputClassName += " input-disabled";
		}

		return (
			<div id={this.props.id} className="field">
				{this.props.name &&
					DashboardUtils.CreateHeaderAndInfoButton(
						this.props.name,
						this.props.info,
						undefined,
						undefined,
						undefined,
						undefined,
						this.props.disabled
					)}
				{timezone_delta_message}
				<div className="control">
					<div className="flex flex-col gap-2">
						<div className="w-full">
							<SeshTextInput
								input_element_override={
									<SeshTextRawInput
										{...{
											placeholder: this.props.placeholder,
											type: "text",
											value: this.state.value,
											onChange: this.handleInputChange,
											disabled: this.props.disabled,
										}}
										right_children={() => (
											<div className="h-full w-full">
												<DatePickerButton
													key={this.state.utc_date?.toISO()}
													initial_date={
														this.state.utc_date?.toJSDate() ??
														new Date()
													}
													onSubmit={(date) => {
														this.updateDateFromPicker(
															date
														);
													}}
													modal_subtitle={
														"Select the event time using your local timezone"
													}
												/>
											</div>
										)}
									/>
								}
								input_props={{}}
							/>
						</div>
						<AddDurationComponent
							guild_zone={this.props.guild_zone}
							personal_zone={this.props.personal_zone}
							use_24h_clock={this.props.use_24h_clock}
						/>
					</div>
				</div>
				{output_message}
			</div>
		);
	}
}

function AddDurationComponent(props: {
	personal_zone: string;
	guild_zone: string;
	use_24h_clock: boolean;
}) {
	const creation_context = useContext(EventCreationContext);

	if (creation_context.state.duration_ms == null) {
		return (
			<div className="ml-auto">
				<ModifySettingsButton
					hide_arrow
					onClick={() =>
						creation_context.setState({
							duration_ms: 1000 * 60 * 60,
						})
					}
				>
					Add Duration
				</ModifySettingsButton>
			</div>
		);
	}

	return (
		<div className="flex w-full items-center gap-1 sm:w-3/4">
			<EventDurationComponent />
		</div>
	);
}
